/* eslint-disable @typescript-eslint/camelcase */
// Payload builders and selectors for the v3 Entanglement quote lifecycle,
// ported from tpr/src/helpers.js and remapped to evo's store shape.
// snake_case identifiers (rate_type, sa1_7dig) are TPR API contract fields.
import { jwtDecode } from 'jwt-decode';
import { blankQuoteCreate } from '@/store/blanks';
import Helpers from './helpers';

function getVehicleModelYear(state: any): string {
	const produced = state.vehicle.produced || '';
	const idx = produced.indexOf('-');
	return idx > 0 ? produced.substring(0, idx) : '';
}

// the live API returns additionalOutput at the top level, tpr's blank kept it
// under customerDomain — accept either
function getAdditionalOutput(updateRepayments: any): any {
	return updateRepayments.additionalOutput ||
		(updateRepayments.alfaFinancialResponse &&
			updateRepayments.alfaFinancialResponse.customerDomain &&
			updateRepayments.alfaFinancialResponse.customerDomain.additionalOutput) ||
		{};
}

// ported from tpr/src/helpers.js (commit dddf7b8e)
export function isUsableNumber(v: any): boolean {
	return typeof v === 'number' && !isNaN(v);
}
// The TPR retrieve response reports kms/year in more than one place, so resolve
// in priority order rather than trusting a single path: whichever field is
// actually populated wins, and if none is we return undefined so the caller can
// keep the value it already had.
export function getRetrievedKmsPerYear(response: any): number | undefined {
	const fdrf = response?.customerDomain?.financialDetailRetailFinance || {};
	const years = isUsableNumber(fdrf.loanTerm) && fdrf.loanTerm > 0 ? fdrf.loanTerm / 12 : 0;
	const candidates = [
		response?.vehicleDomain?.vehicle?.estimatedDistance,
		fdrf.estimatedDistance,
		// what QuoteCreate posts as endOfTermOdometerReading is kmsPerYear * years
		years && isUsableNumber(fdrf.endOfTermOdometerReading) ? fdrf.endOfTermOdometerReading / years : undefined
	];
	const kms = candidates.find(v => isUsableNumber(v) && v > 0);
	return isUsableNumber(kms) ? kms : undefined;
}
// The finance settings a retrieved quote was saved with. Only keys that resolved
// to a usable number are present, so callers can spread this over current state
// without ever overwriting a good value with undefined.
export function getRetrievedFinanceSettings(response: any): any {
	const fdrf = response?.customerDomain?.financialDetailRetailFinance || {};
	const fa = response?.customerDomain?.financeAccount || {};
	const price = response?.vehicleDomain?.vehiclePrice?.basePrice;
	const obj: any = {};

	if (isUsableNumber(response?.loanType) && response.loanType){
		obj.financeType = response.loanType;
	}
	const kmsPerYear = getRetrievedKmsPerYear(response);
	if (isUsableNumber(kmsPerYear)){
		obj.kmsPerYear = kmsPerYear;
	}
	if (isUsableNumber(fdrf.loanTerm) && fdrf.loanTerm > 0){
		obj.loanTerm = fdrf.loanTerm / 12;
	}
	if (isUsableNumber(fa.depositAmount) && fa.depositAmount >= 0){
		obj.financeDeposit = fa.depositAmount;
	}
	if (isUsableNumber(fa.calculatedBalloonAmount) && isUsableNumber(price) && price > 0){
		obj.balloonPercentage = Math.round(fa.calculatedBalloonAmount / price * 100);
	}
	return obj;
}
export function getLoadedFinance(state: any): any {
	let obj1: any = {};
	let obj2: any = {};
	let loanType = 0;
	let estimatedDistance = 0;
	const qcFDRF = state.quoteCreate.createQuoteResponse.customerDomain.financialDetailRetailFinance;
	const qcFA = state.quoteCreate.createQuoteResponse.customerDomain.financeAccount;
	const rqFDRF = state.retrieveQuote.response.customerDomain.financialDetailRetailFinance;
	const rqFA = state.retrieveQuote.response.customerDomain.financeAccount;
	const blFDRF = blankQuoteCreate.createQuoteResponse.customerDomain.financialDetailRetailFinance;
	const blFA = {
		balloonAmount: 0,
		calculatedBalloonAmount: 0,
		depositAmount: 0,
		totalAmountFinanced: 0
	};
	if (rqFDRF && rqFDRF.interestRate){
		obj1 = rqFDRF;
		obj2 = rqFA;
		loanType = state.retrieveQuote.response.loanType;
		estimatedDistance = getRetrievedKmsPerYear(state.retrieveQuote.response) || 0;
	}
	else if (qcFDRF && qcFDRF.interestRate){
		obj1 = qcFDRF;
		obj2 = qcFA;
		loanType = state.app.financeType;
		estimatedDistance = getAdditionalOutput(state.updateRepayments).kmsPerYear;
	}
	else {
		obj1 = JSON.parse(JSON.stringify(blFDRF));
		obj2 = JSON.parse(JSON.stringify(blFA));
	}
	return {...obj1, ...obj2, loanType, estimatedDistance};
}

export function getUpdateRepaymentsObj(state: any, $route: any): any {
	const dealerID = state.dealer.dealerID;
	const kmsTotal = state.app.kmsPerYear * state.app.loanTerm;
	const termInMonths = 12 * state.app.loanTerm;
	const repaymentFrequency = 52;
	const interestRate = state.finance.apr;
	const totalKms = state.finance.financeType == 1 ? kmsTotal : 0;
	const kmsPerYear = state.finance.financeType == 1 ? state.app.kmsPerYear : 0;
	const balloonAmount =
		state.finance.financeType === 1
			? state.finance.gfv
			: state.finance.balloon;
	const depositAmount = state.finance.financeDeposit;
	const accessToken = state.forgeRockApp.tokens && state.forgeRockApp.tokens.accessToken;
	const loanType = state.finance.financeType;
	const retrieveQuoteUrl = Helpers.getRestoreUrl(state, $route);
	const expiryDate = getTprExpiryDate(state);
	return {
		dealerID,
		termInMonths,
		repaymentFrequency,
		interestRate,
		totalKms,
		kmsPerYear,
		balloonAmount,
		depositAmount,
		accessToken,
		loanType,
		retrieveQuoteUrl,
		expiryDate
	};
}

// UpdateRepayments requires the TPR expiry as a date-only string
// ("2026-09-24"). quoteCreate.validityDate is the rate's expiry from the
// quote create response, back-filled from FinanceCalculation's rateExpiry
// when the quote was restored or created earlier.
export function getTprExpiryDate(state: any): string {
	return toIsoDateString(state.quoteCreate.validityDate || state.finance.rateExpiry);
}
// "2026-09-24T00:00:00" -> "2026-09-24". String slice on purpose: Date/Moment
// parse as UTC or local and can shift the day for AU users. Anything that
// doesn't start with YYYY-MM-DD is passed through untouched.
export function toIsoDateString(s: any): string {
	const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(s || ''));
	return m ? m[1] : (s || '');
}

export function getQuoteCreateObj(state: any, create: boolean): any {
	const endOdometer = (state.app.kmsPerYear * state.app.loanTerm);
	const termInMths = (state.app.loanTerm * 12);
	const balloonValue = state.finance.financeType === 1 ? state.finance.gfv : state.finance.balloon;
	const postObject: any = {
		dealerId: state.dealer.dealerID,
		vehicleFinanceInformation: {
			vehicleBasePrice: state.vehicle.price,
			optionsAccessoriesTotal: 0,
			otherVehiclePriceSubTotal: 0,
			depositTotal: state.finance.depositTotal || 0,
			tradeInTotal: 0,
			chargesTotal: 0,
			insuranceTotal: 0,
			vehicleName: state.vehicle.model,
			vehicleVariant: state.vehicle.fullDescription,
			vehicleMake: 'TOYOTA',
			vehicleModelYear: getVehicleModelYear(state),
			vehicleProductionDate: state.vehicle.produced + "T00:00:00",
			vehicleMaterialCode: state.vehicle.materialCode,
			isJointApplication: false,
			loanTermInMonths: termInMths,
			balloonAmount: balloonValue,
			interestRate: state.finance.apr,
			comparisonRate: state.finance.comparisonRate,
			repaymentAmount: state.finance.monthlyPayment,
			endOfTermOdometerReading: endOdometer,
			rate_type: "", //deliberately blank for future use.
			campaign: "", //deliberately blank for future use.
			ageGroup: "new", //deliberately blank for future use.
			materialCode: state.vehicle.materialCode,
			enquirySource: state.app.enquirySource.value,
			vehicleImage: state.vehicle.image,
			expectedPurchase: state.customer.expectedPurchase,
			ContactMethod: state.customer.contactMethod == "Mobile" ? 1 : 2,
			vehicleStockIndicator: !!state.vehicle.batchNumber,
			vin: state.vehicle.vin,
			dealerAgencyFee: state.finance.dafQuantum,
			financeType: state.finance.financeType,
			nvic: state.finance.nv,
			vehicleSpecificationID: state.vehicle.vehicleSpecificationID,
			batchNumber: state.vehicle.batchNumber
		},
		accessToken: state.forgeRockApp.tokens.accessToken,
	};
	if (create){
		const wsid = state.dealer.dealerSites.filter((o: any) => o.Suburb == state.customer.preferredDealership).map((o: any) => o.WholesaleId).toString();
		postObject.stockDealerId = state.dealer.stockDealerID;
		postObject.guestInformation = {
			firstName: state.customer.firstName,
			lastName: state.customer.lastName,
			email: state.customer.email,
			// evo stores the phone display-formatted; the API wants it bare
			telephone: (state.customer.phone || '').replace(/\s/g, ''),
			dateofBirth: state.customer.dob + "T00:00:00",
			currAddressOwnerShip: state.customer.livingStatus,
			driversLicenceNumber: state.customer.licenseNumber,
			driversLicenceState: state.customer.licenseOrigin == 'Overseas' ? '' : state.customer.licenseState,
			currSa1Code: state.customer.sa1Code,
			fullAddress: state.customer.address,
			streetAddress: state.customer.streetAddress,
			address: {
				sa1_7dig: state.customer.sa1Code,
				addressline1: state.customer.streetAddress,
				unitNumber: state.customer.unitNo,
				subPremise: state.customer.subPremise,
				suburb: state.customer.suburb,
				state: state.customer.state,
				streetNumber: state.customer.streetNo,
				"streetName": state.customer.streetName,
				"streetType": state.customer.streetType,
				"postCode": state.customer.postcode
			},
			otherProperty: !!state.customer.otherProperty,
			bureauConsent: !!state.customer.privacyAcceptedTpr,
			wholesaleID: wsid ? wsid : '',
		};
	}
	else {
		const accessToken = state.forgeRockApp.tokens.accessToken;
		const ogObj: any = accessToken ? jwtDecode(accessToken) : {};
		postObject.caseID = state.quoteCreate.pegaCaseTracking;
		postObject.quoteGuid = ogObj.omni_guid;
	}
	return postObject;
}

export function getPreApprovalObj(state: any): any {
	const updateCI = state.updateRepayments.alfaFinancialResponse.customerDomain.customerInteraction;
	const retrieveCI = state.retrieveQuote.response.customerDomain.customerInteraction;
	const lastSavedQuote = state.lastSavedQuote;
	if (updateCI.quote.eligibilityOutcome){
		return {
			"quote": {
				"eligibilityId": updateCI.quote.eligibilityId,
				"eligibilityOutcome": updateCI.quote.eligibilityOutcome,
			},
			"preApproval": {
				"preApprovalStatus": updateCI.preApproval && updateCI.preApproval.preApprovalStatus
			}
		};
	}
	else if (retrieveCI.quote.eligibilityOutcome){
		return {
			"quote": {
				"eligibilityId": retrieveCI.quote.eligibilityId,
				"eligibilityOutcome": retrieveCI.quote.eligibilityOutcome,
			},
			"preApproval": {
				"preApprovalStatus": retrieveCI.preApproval && retrieveCI.preApproval.preApprovalStatus
			}
		};
	}
	else if (lastSavedQuote.eligibilityOutcome){
		return {
			"quote": {
				"eligibilityId": lastSavedQuote.eligibilityID,
				"eligibilityOutcome": lastSavedQuote.eligibilityOutcome,
			},
			"preApproval": {
				"preApprovalStatus": lastSavedQuote.preApprovalStatus
			}
		};
	}
	else return {
		"quote": {
			"eligibilityId": "",
			"eligibilityOutcome": "",
		},
		"preApproval": {
			"preApprovalStatus": ""
		}
	};
}

// Everything the conflict checks below compare (vehicle, product, deposit,
// term, kms / balloon) as one string, so a successful save can be remembered
// and not repeated while the selection stays the same. UpdateQuote /
// UpdateRepayments return nothing that could refresh retrieveQuote.response,
// so without this the same differences are detected again at the next save
// point (TprAdjust Continue, then 04 Summary).
export function getSelectionSignature(state: any): string {
	return JSON.stringify({
		nvic: state.finance.nv,
		builddate: state.vehicle.produced,
		price: state.vehicle.price,
		financeLoanType: state.finance.financeType,
		loanType: state.app.financeType,
		deposit: state.app.financeDeposit,
		loanTerm: state.app.loanTerm,
		kmsOrBalloon: state.app.financeType == 4 ? state.finance.balloon : state.app.kmsPerYear
	});
}

export function hasQuoteConflict(state: any): boolean {
	let isConflicting = false;
	const v = state.vehicle;
	const f = state.finance;
	const r = state.retrieveQuote.response;
	const vd = r.vehicleDomain;
	const loadedVehicle = {
		nvic: f.nv,
		builddate: v.produced,
		price: v.price,
		loanType: f.financeType
	};
	const retrievedVehicle = {
		nvic: vd.vehicle.vehicleCode,
		builddate: vd.vehicle.vehicleBuildDate,
		price: vd.vehiclePrice.basePrice,
		loanType: r.loanType
	};

	if (loadedVehicle.nvic != retrievedVehicle.nvic){
		console.log('different nvic', loadedVehicle.nvic, retrievedVehicle.nvic);
		isConflicting = true;
	}
	if (loadedVehicle.builddate != retrievedVehicle.builddate){
		console.log('different build date', loadedVehicle.builddate, retrievedVehicle.builddate);
		isConflicting = true;
	}
	const diff = Math.abs(loadedVehicle.price - retrievedVehicle.price);
	if (diff > 1){
		console.log('price different', loadedVehicle.price, retrievedVehicle.price);
		isConflicting = true;
	}
	// a loan type change is a quote conflict (UpdateQuote then UpdateRepayments),
	// not just a repayment conflict
	if (loadedVehicle.loanType != retrievedVehicle.loanType){
		console.log('loantype different', loadedVehicle.loanType, retrievedVehicle.loanType);
		isConflicting = true;
	}

	console.log('hasQuoteConflict', isConflicting, retrievedVehicle, loadedVehicle);
	return isConflicting;
}

export function hasRepaymentConflict(state: any): boolean {
	let isConflicting = false;
	const fd = getLoadedFinance(state);
	const loadedFinance = {
		deposit: fd.depositAmount,
		loanTerm: fd.loanTerm ? fd.loanTerm / 12 : 0,
		kmsOrBalloon: fd.loanType == 4 ? fd.balloonAmount : fd.estimatedDistance,
		loanType: fd.loanType
	};
	const currentFinance = {
		deposit: state.app.financeDeposit,
		// the user's selected term (years) — the vehicle's initial term is not
		// reactive, comparing against it makes saves silently no-op mid-session
		loanTerm: state.app.loanTerm,
		kmsOrBalloon: state.app.financeType == 4 ? state.finance.balloon : state.app.kmsPerYear,
		loanType: state.app.financeType
	};
	if (loadedFinance.loanType != currentFinance.loanType ||
		loadedFinance.loanTerm != currentFinance.loanTerm ||
		loadedFinance.kmsOrBalloon != currentFinance.kmsOrBalloon ||
		loadedFinance.deposit != currentFinance.deposit
	){
		isConflicting = true;
	}
	return isConflicting;
}

export function getAlphaFinance(state: any): any {
	const updateQuote = state.updateQuote;
	const updateRepayments = state.updateRepayments;
	const retrieveQuote = state.retrieveQuote;
	const quoteCreate = state.quoteCreate;
	const app = state.app;
	const vehicle = state.vehicle;
	const obj: any = {
		payment: -1,
		repaymentAmount: -1,
		comparisonRate: -1,
		interestRate: -1,
		monthlyRepaymentAmount: -1,
		loanTerm: -1,
		disclaimer: "",
		calculatedBalloonAmount: -1, // alpha balloon amount for display
		financeHeading: "",
		paymentFrequency: "",
		img: "",
		vehicleName: "",
		financeType: 0,
		expiryDate: "",
		totalAmountFinanced: 0,
		fees: {
			establishmentFee: 0,
			ppsrFee: 0,
			accountFee: 0,
			onChargedDealerAgencyFee: 0
		}
	};
	if (updateRepayments.success){
		const cd = updateRepayments.alfaFinancialResponse.customerDomain;
		const fdrf = cd.financialDetailRetailFinance;
		const additionalOutput = getAdditionalOutput(updateRepayments);

		obj.payment = fdrf.repaymentAmount;
		obj.paymentFrequency = fdrf.repaymentFrequency || "";
		obj.comparisonRate = fdrf.comparisonRate;
		obj.interestRate = fdrf.interestRate;
		obj.loanTerm = app.loanTerm;
		obj.kms = additionalOutput.totalKms;
		obj.calculatedBalloonAmount = cd.financeAccount.calculatedBalloonAmount;
		obj.disclaimer = additionalOutput.disclaimer;
		obj.financeHeading = additionalOutput.financeHeading;
		obj.vehicleName = vehicle.model;
		obj.financeType = app.financeType; // no finance type in update.
		obj.expiryDate = quoteCreate.validityDate || "";
		obj.totalAmountFinanced = cd.financeAccount.totalAmountFinanced;
		if (cd.customerInteraction && cd.customerInteraction.quote){
			const q = cd.customerInteraction.quote;
			obj.fees = {
				establishmentFee: q.establishmentFee,
				ppsrFee: q.ppsrFee,
				accountFee: q.accountFee,
				onChargedDealerAgencyFee: q.onChargedDealerAgencyFee
			};
		}
	}
	else if (retrieveQuote.success){
		const cd = retrieveQuote.response.customerDomain;
		const fdrf = cd.financialDetailRetailFinance;

		obj.payment = fdrf.repaymentAmount;
		obj.paymentFrequency = fdrf.repaymentFrequency || "";
		obj.comparisonRate = fdrf.comparisonRate;
		obj.interestRate = fdrf.interestRate;
		obj.loanTerm = fdrf.loanTerm;
		obj.kms = -1;
		obj.calculatedBalloonAmount = cd.financeAccount.calculatedBalloonAmount;
		obj.disclaimer = retrieveQuote.response.disclaimer;
		obj.financeHeading = retrieveQuote.response.financeHeading;
		obj.vehicleName = retrieveQuote.response.vehicleDomain.vehicleSpecification.vehicleModel;
		obj.financeType = retrieveQuote.response.loanType;
		// the saved quote's own validity, not the loaded vehicle's rateExpiry
		obj.expiryDate = cd.customerInteraction?.quote?.expiryDate || "";
		obj.totalAmountFinanced = cd.financeAccount.totalAmountFinanced;
		if (cd.customerInteraction && cd.customerInteraction.quote){
			const q = cd.customerInteraction.quote;
			obj.fees = {
				establishmentFee: q.establishmentFee,
				ppsrFee: q.ppsrFee,
				accountFee: q.monthlyAccountFee || 0,
				onChargedDealerAgencyFee: q.onChargedDealerAgencyFee
			};
		}
	}
	else {
		console.log('no alpha finance loaded 2');
	}
	try {
		obj.img = quoteCreate.success || updateQuote.success ? vehicle.image : atob(retrieveQuote.response.vehicleDomain.vehicleSpecification.vehicleImage);
	} catch (e) {
		obj.img = vehicle.image;
	}
	return obj;
}

export function getUseAlpha(state: any): boolean {
	const hasAlphaData = !!(state.updateRepayments.success || state.retrieveQuote.success);
	// currentRouteName mirrors $route.name into the store (App.vue routeName
	// watcher), keeping this route check reactive from the useAlpha getter
	if (state.app.currentRouteName == 'TprAdjust' && !state.app.tprFormSubmitted) return false;
	// an Open-Ineligible pre-approval means the saved quote can't be updated
	// (isQuoteAbleToSave), so its Alfa figures are never shown: the customer
	// sees the local estimate, and the summary hides the pre-approval / OFAP
	// banners (PreApprovalBanner.preApprovalMessage, SummaryPage.showOfap)
	if (getPreApprovalObj(state).preApproval.preApprovalStatus == Helpers.preApprovalEnums.ineligible) return false;
	if (state.app.viewCurrentQuote && hasAlphaData) return true;
	// the alpha figures belong to the saved quote: once the car or finance
	// settings differ from it they are an estimate again until re-saved
	if (hasAlphaData && !isSelectionSyncedWithQuote(state)) return false;
	return !state.app.useEstimateInsteadOfAlpha && hasAlphaData;
}

// does the loaded car + finance selection match the saved quote? Compared
// against app.alphaSelection when a save / conflict-free retrieve recorded
// one (UpdateQuote / UpdateRepayments never refresh retrieveQuote.response,
// so the retrieved quote is stale right after a save); otherwise against the
// retrieved quote itself via the same checks the save flow uses
export function isSelectionSyncedWithQuote(state: any): boolean {
	if (state.app.alphaSelection){
		return state.app.alphaSelection === getSelectionSignature(state);
	}
	if (!state.retrieveQuote.success) return true;
	return !hasQuoteConflict(state) && !hasRepaymentConflict(state);
}

// Pascal-case overrides for the Order/TradeIn payloads: same server contract,
// but rate/repayment/fee figures come from the quote backend when a quote is
// active. Spread AFTER the legacy finance fields.
export function getAlphaOrderOverrides(state: any): any {
	if (!getUseAlpha(state)) return {};
	const a = getAlphaFinance(state);
	const overrides: any = {
		ComparisonRate: a.comparisonRate,
		GFV: a.calculatedBalloonAmount,
		APR: a.interestRate,
		MonthlyRepayment: a.payment,
		LoanEstablishmentFee: a.fees.establishmentFee,
		PPSRFee: a.fees.ppsrFee,
		MonthlyAccountAdministrationFee: a.fees.accountFee,
		DealerAgencyFeeIncGST: a.fees.onChargedDealerAgencyFee
	};
	if (a.totalAmountFinanced){
		overrides.TotalAmountFinanced = a.totalAmountFinanced;
	}
	if (a.disclaimer){
		overrides.FinanceDisclaimer = a.disclaimer;
	}
	return overrides;
}
